import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Eye, Trash2, Loader2, Globe, Music, Upload, X, ExternalLink, Link as LinkIcon, Users, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const HUB_TYPES = ['creator', 'dj', 'promoter', 'venue', 'brand'];
const GENRES = ['House', 'Techno', 'Hip Hop', 'R&B', 'Latin', 'Indie', 'Rock', 'Jazz', 'Electronic', 'Pop', 'Country', 'Reggae', 'Afrobeats'];

export default function CreatorHubDashboard() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingHub, setEditingHub] = useState(null);
  const [handleAvailable, setHandleAvailable] = useState(null);
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [selectedHub, setSelectedHub] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [tierForm, setTierForm] = useState({ name: '', description: '', price_monthly: 0, perks: [], badge_emoji: '', badge_color: '#6366f1' });
  const [form, setForm] = useState({
    stage_name: '', handle: '', tagline: '', bio: '', hub_type: 'creator',
    city: 'Denver', state: 'CO', genres: [], avatar: '', cover_image: '',
    booking_email: '', booking_url: '', accent_color: '#6366f1',
    social_links: { instagram: '', youtube: '', spotify: '', soundcloud: '', tiktok: '', twitter: '' },
    links: [], featured_media: []
  });

  useEffect(() => {
    if (!isAuthenticated) { navigate('/auth'); return; }
    fetchHubs();
  }, [isAuthenticated]);

  const fetchHubs = async () => {
    try { const res = await api.get('/creators/my-hubs'); setHubs(res.data.hubs || []); } catch {} finally { setLoading(false); }
  };

  const checkHandle = async (handle) => {
    if (handle.length < 3) { setHandleAvailable(null); return; }
    try { const res = await api.post('/creators/check-handle', { handle }); setHandleAvailable(res.data.available); } catch { setHandleAvailable(null); }
  };

  const handleSave = async () => {
    if (!form.stage_name || !form.handle) { toast.error('Stage name and handle required'); return; }
    setSaving(true);
    try {
      if (editingHub) {
        await api.put(`/creators/hub/${editingHub.id}`, form);
        toast.success('Hub updated');
      } else {
        await api.post('/creators/hub', form);
        toast.success('Hub created!');
      }
      setShowDialog(false);
      setEditingHub(null);
      fetchHubs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const openCreate = () => {
    setEditingHub(null);
    setForm({ stage_name: '', handle: '', tagline: '', bio: '', hub_type: 'creator', city: 'Denver', state: 'CO', genres: [], avatar: '', cover_image: '', booking_email: '', booking_url: '', accent_color: '#6366f1', social_links: { instagram: '', youtube: '', spotify: '', soundcloud: '', tiktok: '', twitter: '' }, links: [], featured_media: [] });
    setHandleAvailable(null);
    setShowDialog(true);
  };

  const openEdit = (hub) => {
    setEditingHub(hub);
    setForm(hub);
    setShowDialog(true);
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Creator Hubs</h1>
            <p className="text-muted-foreground">Your branded profiles on EVENT★GOERS</p>
          </div>
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Create Hub</Button>
        </div>

        {hubs.length === 0 ? (
          <Card className="p-12 text-center">
            <Globe className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No hubs yet</h3>
            <p className="text-muted-foreground mb-6">Create your first creator hub — a branded profile page with your events, media, and links.</p>
            <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> Create Your Hub</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {hubs.map(hub => (
              <Card key={hub.id} className="overflow-hidden">
                <CardContent className="p-0 flex items-center">
                  <div className="w-20 h-20 shrink-0 bg-slate-100 flex items-center justify-center" style={{ backgroundColor: `${hub.accent_color}22` }}>
                    {hub.avatar ? <img src={hub.avatar} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl font-bold" style={{ color: hub.accent_color }}>{hub.stage_name?.charAt(0)}</span>}
                  </div>
                  <div className="p-4 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold">{hub.stage_name}</h3>
                      <Badge variant="outline" className="text-xs">@{hub.handle}</Badge>
                      <Badge variant="secondary" className="text-xs">{hub.hub_type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{hub.tagline || 'No tagline'}</p>
                    <p className="text-xs text-muted-foreground mt-1">{hub.followers_count || 0} followers · {hub.city}</p>
                  </div>
                  <div className="flex gap-2 p-4">
                    <Button variant="ghost" size="sm" onClick={() => window.open(`/creators/${hub.handle}`, '_blank')}><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(hub)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedHub(hub); setShowTierDialog(true); }}>Tiers</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingHub ? 'Edit Hub' : 'Create Creator Hub'}</DialogTitle></DialogHeader>
          
          <Tabs defaultValue="profile">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="links">Links</TabsTrigger>
              <TabsTrigger value="media">Media</TabsTrigger>
              <TabsTrigger value="style">Style</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Stage Name *</Label><Input value={form.stage_name} onChange={e => { setForm({...form, stage_name: e.target.value}); if (!editingHub) { const h = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''); setForm(prev => ({...prev, handle: h})); checkHandle(h); } }} placeholder="DJ Shadow" /></div>
                <div><Label>Handle *</Label>
                  <div className="relative"><Input value={form.handle} onChange={e => { setForm({...form, handle: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')}); checkHandle(e.target.value); }} placeholder="djshadow" className="pl-8" /><span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span></div>
                  {handleAvailable !== null && <p className={`text-xs mt-1 ${handleAvailable ? 'text-green-500' : 'text-red-500'}`}>{handleAvailable ? 'Available!' : 'Taken'}</p>}
                  <p className="text-[10px] text-muted-foreground">eventgoers.com/creators/{form.handle || '...'}</p>
                </div>
              </div>
              <div><Label>Tagline</Label><Input value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} placeholder="Denver's #1 House Music DJ" /></div>
              <div><Label>Bio</Label><Textarea value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} rows={3} placeholder="Tell your story..." /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Type</Label><Select value={form.hub_type} onValueChange={v => setForm({...form, hub_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{HUB_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
                <div><Label>State</Label><Input value={form.state} onChange={e => setForm({...form, state: e.target.value})} /></div>
              </div>
              <div><Label>Genres</Label>
                <div className="flex flex-wrap gap-2 mt-1">{GENRES.map(g => <Badge key={g} variant={(form.genres||[]).includes(g) ? 'default' : 'outline'} className="cursor-pointer text-xs" onClick={() => setForm({...form, genres: (form.genres||[]).includes(g) ? form.genres.filter(x=>x!==g) : [...(form.genres||[]), g]})}>{g}</Badge>)}</div>
              </div>
              <div><Label>Booking Email</Label><Input type="email" value={form.booking_email} onChange={e => setForm({...form, booking_email: e.target.value})} placeholder="booking@example.com" /></div>
              <div><Label>Booking URL</Label><Input value={form.booking_url} onChange={e => setForm({...form, booking_url: e.target.value})} placeholder="https://..." /></div>
            </TabsContent>

            <TabsContent value="links" className="space-y-4 mt-4">
              <div><Label>Social Links</Label>
                <div className="space-y-2 mt-2">
                  {['instagram', 'youtube', 'spotify', 'soundcloud', 'tiktok', 'twitter'].map(platform => (
                    <div key={platform} className="flex items-center gap-2"><span className="w-24 text-sm capitalize text-muted-foreground">{platform}</span><Input value={(form.social_links||{})[platform] || ''} onChange={e => setForm({...form, social_links: {...form.social_links, [platform]: e.target.value}})} placeholder={`https://${platform}.com/...`} className="flex-1" /></div>
                  ))}
                </div>
              </div>
              <div><Label>Custom Links</Label>
                {(form.links||[]).map((link, i) => (
                  <div key={i} className="flex gap-2 mt-2">
                    <Input value={link.title} placeholder="Title" onChange={e => { const l=[...form.links]; l[i]={...l[i], title: e.target.value}; setForm({...form, links: l}); }} className="flex-1" />
                    <Input value={link.url} placeholder="URL" onChange={e => { const l=[...form.links]; l[i]={...l[i], url: e.target.value}; setForm({...form, links: l}); }} className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => setForm({...form, links: form.links.filter((_,idx)=>idx!==i)})}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setForm({...form, links: [...(form.links||[]), {title:'', url:'', order: (form.links||[]).length}]})}><Plus className="w-3 h-3 mr-1" /> Add Link</Button>
              </div>
            </TabsContent>

            <TabsContent value="media" className="space-y-4 mt-4">
              <div><Label>Avatar</Label>
                <div className="flex gap-2 mt-1"><Input value={form.avatar} onChange={e => setForm({...form, avatar: e.target.value})} placeholder="Image URL" className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.onchange = async (ev) => { const file = ev.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file); try { const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setForm(prev => ({...prev, avatar: res.data.url})); toast.success('Uploaded'); } catch { toast.error('Failed'); } }; input.click(); }}><Upload className="w-4 h-4" /></Button></div>
              </div>
              <div><Label>Cover Image</Label>
                <div className="flex gap-2 mt-1"><Input value={form.cover_image} onChange={e => setForm({...form, cover_image: e.target.value})} placeholder="Image URL" className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.onchange = async (ev) => { const file = ev.target.files?.[0]; if (!file) return; const fd = new FormData(); fd.append('file', file); try { const res = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setForm(prev => ({...prev, cover_image: res.data.url})); toast.success('Uploaded'); } catch { toast.error('Failed'); } }; input.click(); }}><Upload className="w-4 h-4" /></Button></div>
              </div>
              <div><Label>Featured Media (mixes, videos, tracks)</Label>
                {(form.featured_media||[]).map((m, i) => (
                  <div key={i} className="flex gap-2 mt-2">
                    <Select value={m.type || 'mix'} onValueChange={v => { const fm=[...form.featured_media]; fm[i]={...fm[i], type: v}; setForm({...form, featured_media: fm}); }}><SelectTrigger className="w-24"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mix">Mix</SelectItem><SelectItem value="video">Video</SelectItem><SelectItem value="track">Track</SelectItem><SelectItem value="photo">Photo</SelectItem></SelectContent></Select>
                    <Input value={m.title} placeholder="Title" onChange={e => { const fm=[...form.featured_media]; fm[i]={...fm[i], title: e.target.value}; setForm({...form, featured_media: fm}); }} className="flex-1" />
                    <Input value={m.url} placeholder="URL" onChange={e => { const fm=[...form.featured_media]; fm[i]={...fm[i], url: e.target.value}; setForm({...form, featured_media: fm}); }} className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => setForm({...form, featured_media: form.featured_media.filter((_,idx)=>idx!==i)})}><X className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setForm({...form, featured_media: [...(form.featured_media||[]), {type:'mix', title:'', url:''}]})}><Plus className="w-3 h-3 mr-1" /> Add Media</Button>
              </div>
            </TabsContent>

            <TabsContent value="style" className="space-y-4 mt-4">
              <div><Label>Accent Color</Label>
                <div className="flex gap-3 mt-2">
                  {['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#D4AF37'].map(c => (
                    <button key={c} className={`w-8 h-8 rounded-full border-2 ${form.accent_color === c ? 'border-white scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => setForm({...form, accent_color: c})} />
                  ))}
                  <Input type="color" value={form.accent_color} onChange={e => setForm({...form, accent_color: e.target.value})} className="w-8 h-8 p-0 border-0" />
                </div>
              </div>
              <div className="p-4 rounded-xl" style={{ backgroundColor: `${form.accent_color}11`, borderColor: `${form.accent_color}44`, border: '1px solid' }}>
                <p className="text-sm text-muted-foreground">Preview: Your hub will use this accent color for buttons, highlights, and interactive elements.</p>
                <Button size="sm" className="mt-2 text-white" style={{ backgroundColor: form.accent_color }}>{form.stage_name || 'Your Name'}</Button>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editingHub ? 'Update Hub' : 'Create Hub'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tier Management Dialog */}
      <Dialog open={showTierDialog} onOpenChange={(open) => { setShowTierDialog(open); if (open && selectedHub) { api.get(`/community/tiers/${selectedHub.id}`).then(r => setTiers(r.data.tiers || [])).catch(() => {}); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Membership Tiers — {selectedHub?.stage_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {tiers.map(tier => (
              <div key={tier.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="font-bold text-sm">{tier.badge_emoji} {tier.name}</p>
                  <p className="text-xs text-muted-foreground">{tier.price_monthly > 0 ? `$${tier.price_monthly}/mo` : 'Free'} · {tier.subscriber_count || 0} members</p>
                </div>
                <Button variant="ghost" size="sm" className="text-red-500" onClick={async () => { try { await api.delete(`/community/tiers/${tier.id}`); setTiers(prev => prev.filter(t => t.id !== tier.id)); toast.success('Deleted'); } catch { toast.error('Failed'); } }}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
          <div className="border-t pt-4 space-y-3">
            <p className="font-medium text-sm">Add New Tier</p>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Name *</Label><Input value={tierForm.name} onChange={e => setTierForm({...tierForm, name: e.target.value})} placeholder="VIP" /></div>
              <div><Label>Price/mo ($)</Label><Input type="number" value={tierForm.price_monthly} onChange={e => setTierForm({...tierForm, price_monthly: parseFloat(e.target.value) || 0})} placeholder="0 = free" /></div>
            </div>
            <div><Label>Description</Label><Input value={tierForm.description} onChange={e => setTierForm({...tierForm, description: e.target.value})} placeholder="What members get..." /></div>
            <div><Label>Perks (comma-separated)</Label><Input value={(tierForm.perks || []).join(', ')} onChange={e => setTierForm({...tierForm, perks: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} placeholder="Exclusive content, Early access, VIP badge" /></div>
            <div className="flex gap-2">
              <div><Label>Emoji</Label><Input value={tierForm.badge_emoji} onChange={e => setTierForm({...tierForm, badge_emoji: e.target.value})} placeholder="⭐" className="w-16" /></div>
              <div><Label>Color</Label><Input type="color" value={tierForm.badge_color} onChange={e => setTierForm({...tierForm, badge_color: e.target.value})} className="w-16 h-10 p-1" /></div>
            </div>
            <Button className="w-full" onClick={async () => {
              if (!tierForm.name || !selectedHub) return;
              try {
                const res = await api.post('/community/tiers', { ...tierForm, hub_id: selectedHub.id, order: tiers.length });
                setTiers(prev => [...prev, res.data]);
                setTierForm({ name: '', description: '', price_monthly: 0, perks: [], badge_emoji: '', badge_color: '#6366f1' });
                toast.success('Tier created');
              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
            }}><Plus className="w-4 h-4 mr-1" /> Add Tier</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
